import { memo } from "react";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Compass, MapPin, MapPinned, Users, Egg } from "lucide-react";

interface TabDef {
  key: string;
  path: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement> & { className?: string }>;
  labelKey: string;
  activePaths: string[];
  activeStartsWith: string[];
}

const tabs: TabDef[] = [
  {
    key: "explore",
    path: "/explore",
    icon: Compass,
    labelKey: "bottomNav.explore",
    activePaths: ["/explore", "/discover", "/festival-guide", "/swipe", "/daily-guide", "/travel-calendar", "/dashboard", "/endeavor"],
    activeStartsWith: ["/route/"],
  },
  {
    key: "the-scene",
    path: "/the-scene",
    icon: MapPinned,
    labelKey: "bottomNav.localVibes",
    activePaths: ["/the-scene", "/local-vibes", "/local-events"],
    activeStartsWith: [],
  },
  {
    key: "trips",
    path: "/my-trips",
    icon: MapPin,
    labelKey: "bottomNav.trips",
    activePaths: ["/my-trips", "/expenses", "/packing", "/create-trip"],
    activeStartsWith: ["/trip/"],
  },
  {
    key: "troop",
    path: "/friends",
    icon: Users,
    labelKey: "bottomNav.social",
    activePaths: ["/friends", "/messages", "/community", "/reviews", "/referrals", "/photos", "/settings", "/transactions", "/points-miles", "/premium", "/rewards", "/favorite-board", "/favorites-leaderboard", "/notifications"],
    activeStartsWith: ["/profile/"],
  },
  {
    key: "troopie",
    path: "/creature",
    icon: Egg,
    labelKey: "bottomNav.creature",
    activePaths: ["/creature"],
    activeStartsWith: [],
  },
];

export const BottomNav = memo(function BottomNav() {
  const [location] = useLocation();
  const { t } = useTranslation();

  const getIsActive = (tab: TabDef): boolean => {
    if (tab.activePaths.includes(location)) return true;
    return tab.activeStartsWith.some((prefix) => location.startsWith(prefix));
  };

  return (
    <nav
      aria-label={t("bottomNav.ariaLabel", "Main navigation")}
      className="fixed bottom-0 left-0 right-0 z-40 sm:hidden overflow-hidden"
      style={{
        background: "var(--nav-bg)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {/* Top border — brass-to-coral gradient indicator */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: "var(--nav-indicator)" }}
        aria-hidden="true"
      />

      <div className="flex items-center justify-around h-14">
        {tabs.map((tab) => {
          const href = tab.path;
          const isActive = getIsActive(tab);

          return (
            <Link
              key={tab.key}
              href={href}
              aria-label={t(tab.labelKey)}
              aria-current={isActive ? "page" : undefined}
              className="flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 min-h-[44px] h-full px-1 relative transition-all duration-200"
              data-testid={`bottom-nav-${tab.key}`}
              data-active={isActive || undefined}
            >
              {/* Active indicator line above icon */}
              {isActive && (
                <span
                  className="absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-8 rounded-b-full"
                  style={{ background: "var(--nav-indicator)" }}
                  aria-hidden="true"
                />
              )}

              <tab.icon
                className="h-5 w-5 transition-all duration-200"
                style={{
                  color: isActive ? "var(--nav-icon-active)" : "var(--nav-icon-inactive)",
                  strokeWidth: isActive ? 2.5 : 1.5,
                  filter: isActive ? "var(--nav-active-glow)" : "none",
                }}
                aria-hidden="true"
              />
              <span
                className="text-[10px] leading-tight font-medium truncate max-w-full transition-colors duration-200"
                style={{
                  color: isActive ? "var(--nav-label-active)" : "var(--nav-label-inactive)",
                }}
              >
                {t(tab.labelKey)}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
});
